import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { FiSave } from "react-icons/fi";
import { DeviceRegion, DeviceStatusResponse } from "types/device.types";
import {
  ModesSidebar,
  ModesTitle,
  ModeToggle,
  ModeToggleOption,
  ModesList,
  ModesCategory,
  CategoryTitle,
  CategoryModes,
  ModeButton,
  BoostModeButton,
} from "styles/devices/deviceModes.styles";

interface OperationModesPanelProps {
  deviceData: DeviceStatusResponse;
  updatingMode: boolean;
  handleSaveModes: (modes: string[]) => void;
  region: DeviceRegion;
}

const OperationModesPanel: React.FC<OperationModesPanelProps> = ({
  deviceData,
  updatingMode,
  handleSaveModes,
  region,
}) => {
  // Local component states
  const [pendingModes, setPendingModes] = useState<string[]>([]);

  // Define mode groups
  const environmentModes = ["WINTER", "SPA", "COVER"];
  const operationsModes = ["BOOST", "BACKWASH", "SERVICE"];

  // Check if ORP is connected
  const isOrpConnected = deviceData?.response?.shadow?.orpConnected || false;

  // Initialize state with current device modes
  useEffect(() => {
    if (deviceData && deviceData.response && deviceData.response.shadow) {
      setPendingModes(deviceData.response.shadow.modes || []);
    }
  }, [deviceData]);

  const savedModes = deviceData?.response?.shadow?.modes || [];
  const hasDirtyModes =
    pendingModes.length !== savedModes.length ||
    pendingModes.some((mode) => !savedModes.includes(mode));

  // Determine which main mode is currently active (MANUAL, SCHEDULE or OFF)
  const hasManualMode = pendingModes.includes("MANUAL");
  const hasScheduleMode = pendingModes.includes("SCHEDULE");
  const mainMode = hasManualMode
    ? "MANUAL"
    : hasScheduleMode
    ? "SCHEDULE"
    : "OFF";

  // Function to toggle a specific mode
  const handleToggleMode = (mode: string) => {
    // If ORP is connected and the mode is COVER, SPA, or WINTER, don't allow activation
    if (isOrpConnected && (mode === "COVER" || mode === "SPA")) {
      return;
    }

    const isActive = pendingModes.includes(mode);

    // Toggle mode (add if not active, remove if active)
    const updatedModes = isActive
      ? pendingModes.filter((m) => m !== mode)
      : [...pendingModes, mode];

    setPendingModes(updatedModes);
  };

  // Function to set the main mode (OFF, MANUAL, SCHEDULE)
  const handleSetMainMode = (newMode: "OFF" | "MANUAL" | "SCHEDULE") => {
    let updatedModes = [...pendingModes];

    if (newMode === "OFF") {
      // For OFF, remove both MANUAL and SCHEDULE
      updatedModes = updatedModes.filter(
        (m) => m !== "MANUAL" && m !== "SCHEDULE"
      );
    } else {
      // For MANUAL or SCHEDULE, add the new mode and remove the other
      updatedModes = updatedModes.filter(
        (m) => m !== (newMode === "MANUAL" ? "SCHEDULE" : "MANUAL")
      );
      updatedModes.push(newMode);
    }

    setPendingModes(updatedModes);
  };

  // Function to save the modified modes
  const onSaveModes = () => {
    handleSaveModes(pendingModes);
  };

  return (
    <ModesSidebar>
      <ModesTitle>
        Operation Mode
        <Button
          type="button"
          icon={<FiSave />}
          label="Save"
          onClick={onSaveModes}
          loading={updatingMode}
          disabled={updatingMode || !hasDirtyModes}
        />
      </ModesTitle>

      {/* Three-state toggle for main operation mode */}
      <ModeToggle>
        <ModeToggleOption
          $active={mainMode === "OFF"}
          onClick={() => handleSetMainMode("OFF")}
        >
          OFF
        </ModeToggleOption>
        <ModeToggleOption
          $active={mainMode === "MANUAL"}
          onClick={() => handleSetMainMode("MANUAL")}
        >
          ON
        </ModeToggleOption>
        {region === DeviceRegion.AU && (
          <ModeToggleOption
            $active={mainMode === "SCHEDULE"}
            onClick={() => handleSetMainMode("SCHEDULE")}
          >
            SCHEDULE
          </ModeToggleOption>
        )}
      </ModeToggle>

      {/* Grouped categories of modes */}
      <ModesList>
        {/* Environment Modes: COVER, SPA, WINTER */}
        <ModesCategory>
          <CategoryTitle>Environment Modes</CategoryTitle>
          <CategoryModes>
            {environmentModes.map((mode) => {
              // Disable COVER, SPA and WINTER when ORP is connected
              const isDisabled =
                isOrpConnected && (mode === "COVER" || mode === "SPA");
              const isActive = pendingModes.includes(mode);

              return (
                <ModeButton
                  key={mode}
                  $active={isActive}
                  title={
                    isDisabled
                      ? `${mode} cannot be activated while ORP sensor is connected`
                      : mode
                  }
                  onClick={() => handleToggleMode(mode)}
                  disabled={isDisabled}
                  style={
                    isDisabled ? { cursor: "not-allowed", opacity: 0.7 } : {}
                  }
                >
                  {mode}
                </ModeButton>
              );
            })}
          </CategoryModes>
        </ModesCategory>

        {/* Special Operations Modes */}
        <ModesCategory>
          <CategoryTitle>Special Operations</CategoryTitle>
          <CategoryModes>
            {operationsModes.map((mode) => {
              const isActive = pendingModes.includes(mode);

              // Use special teal button for BOOST mode
              if (mode === "BOOST") {
                return (
                  <BoostModeButton
                    key={mode}
                    $active={isActive}
                    title={mode}
                    onClick={() => handleToggleMode(mode)}
                  >
                    {mode}
                  </BoostModeButton>
                );
              }

              // Use regular button for other modes
              return (
                <ModeButton
                  key={mode}
                  disabled
                  $active={isActive}
                  title={mode}
                  onClick={() => {}}
                  style={{ cursor: "not-allowed" }}
                >
                  {mode}
                </ModeButton>
              );
            })}
          </CategoryModes>
        </ModesCategory>
      </ModesList>
    </ModesSidebar>
  );
};

export default OperationModesPanel;
